import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FieldService } from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { CheckboxComponent } from '@components/checkbox';
import { DatePickerComponent } from '@components/date-picker/date-picker.component';
import { MsfSelect } from '@components/select/select';
import { MsfSelectPaginated } from '@components/select/paginated/select-paginated';
import { TextareaComponent } from '@components/textarea';
import { MsfInput } from '@components/ui/forms/input/input';
import { RegionDictionarySelectComponent } from '@app/pages/admin/components/region-dictionary-select/region-dictionary-select';
import {
  ASSIGNMENT_DATE_PLACEHOLDER_OPTIONS,
  ASSIGNMENT_DATE_SIGN_OPTIONS,
  ASSIGNMENT_DATE_UNIT_OPTIONS,
  AssignmentStartFormValue,
  AssignmentStartKind,
  GEOMETRY_SOURCE_OPTIONS,
  PRIORITY_OPTIONS,
  assignmentDateFieldState,
  assignmentStartFormFromJson,
  assignmentDateFromMode,
  assignmentDateFromOffset,
  assignmentStartKind,
  buildAssignmentStartParameters,
  canStartAssignment,
  defaultAssignmentStartForm,
} from '@app/pages/admin/assignment-start.util';
import { DATE_PLACEHOLDER_EXAMPLES } from '@app/pages/admin/process-subscription-date.util';
import { orchestrationSatelliteProductUrl } from '@app/pages/admin/orchestration-identity.util';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { OrchestrationGeometryMapComponent } from '@app/pages/admin/components/orchestration-geometry-map/orchestration-geometry-map';
import { LOCALE_DEFAULT_END_ISO, LOCALE_DEFAULT_START_ISO } from '@app/utils/locale-date.util';

@Component({
  selector: 'app-assignment-start-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    CheckboxComponent,
    DatePickerComponent,
    MsfInput,
    MsfSelect,
    MsfSelectPaginated,
    RegionDictionarySelectComponent,
    TextareaComponent,
    CopyableIdentifierComponent,
    OrchestrationGeometryMapComponent,
  ],
  templateUrl: './assignment-start-form.html',
  styleUrl: './assignment-start-form.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentStartFormComponent {
  readonly processKey = input.required<string>();
  readonly seedJson = input<string | null>(null);
  readonly form = signal<AssignmentStartFormValue>(defaultAssignmentStartForm(''));
  readonly ready = signal(false);

  readonly kind = computed<AssignmentStartKind>(() => assignmentStartKind(this.processKey()));
  readonly satelliteProductAnalysisUrl = computed(() =>
    this.kind() === 'post-processing' ? orchestrationSatelliteProductUrl(this.form().satelliteProductId) : null,
  );
  readonly showCloudCover = computed(() => this.processKey() === 'satellite.download-found-products');
  readonly showCountryName = computed(() => this.form().geometrySource === 'CountryName');
  readonly showRegionIds = computed(() => this.form().geometrySource === 'Regions');
  readonly showGeoJson = computed(() => this.form().geometrySource === 'GeoJson');
  readonly showGeoJsonFile = computed(() => this.form().geometrySource === 'GeoJsonFile');
  readonly showFieldId = computed(() => this.processKey() === 'satellite.calculate-season-indices');
  readonly showForce = computed(() => this.processKey() === 'satellite.backfill-season-indices');
  readonly showDetectionJob = computed(() => this.processKey() === 'satellite.backfill-season-indices');

  readonly geometryOptions = GEOMETRY_SOURCE_OPTIONS;
  readonly priorityOptions = PRIORITY_OPTIONS;
  readonly datePlaceholderOptions = ASSIGNMENT_DATE_PLACEHOLDER_OPTIONS;
  readonly dateSignOptions = ASSIGNMENT_DATE_SIGN_OPTIONS;
  readonly dateUnitOptions = ASSIGNMENT_DATE_UNIT_OPTIONS;
  readonly datePlaceholderExamples = DATE_PLACEHOLDER_EXAMPLES;
  readonly fieldService = inject(FieldService);
  readonly startDateField = computed(() => assignmentDateFieldState(this.form().startDate));
  readonly endDateField = computed(() => assignmentDateFieldState(this.form().endDate));
  readonly startDateIsCalendar = computed(() => !this.startDateField().showOffset);
  readonly endDateIsCalendar = computed(() => !this.endDateField().showOffset);

  constructor() {
    effect(() => {
      const processKey = this.processKey();
      const seedJson = this.seedJson();
      untracked(() => this.resetFor(processKey, seedJson));
    });
  }

  reset(): void {
    this.resetFor(this.processKey(), this.seedJson());
  }

  build() {
    return buildAssignmentStartParameters(this.processKey(), this.form());
  }

  setName(value: string): void {
    this.patch({ name: value });
  }

  setGeometrySource(value: string): void {
    this.patch({ geometrySource: value ?? 'CountryBoundaries' });
  }

  setRegionIds(value: string[]): void {
    this.patch({ regionIds: value });
  }

  setCountryName(value: string): void {
    this.patch({ countryName: value });
  }

  setGeoJson(value: string): void {
    this.patch({ geoJson: value });
  }

  setGeoJsonFromMap(value: string): void {
    this.patch({ geometrySource: 'GeoJson', geoJson: value });
  }

  setGeoJsonFileId(value: string): void {
    this.patch({ geoJsonFileId: value });
  }

  setStartDateMode(value: string): void {
    this.patch({
      startDate: assignmentDateFromMode(value, LOCALE_DEFAULT_START_ISO, this.form().startDate),
    });
  }

  setStartDate(value: Date | string | null): void {
    this.patch({ startDate: value });
  }

  setStartDateSign(value: string): void {
    this.patch({
      startDate: assignmentDateFromOffset(this.form().startDate, { sign: value === '+' ? '+' : '-' }),
    });
  }

  setStartDateAmount(value: unknown): void {
    this.patch({
      startDate: assignmentDateFromOffset(this.form().startDate, { amount: value as number }),
    });
  }

  setStartDateUnit(value: string): void {
    this.patch({
      startDate: assignmentDateFromOffset(this.form().startDate, {
        unit: value === 'h' || value === 'm' ? value : 'd',
      }),
    });
  }

  setEndDateMode(value: string): void {
    this.patch({
      endDate: assignmentDateFromMode(value, LOCALE_DEFAULT_END_ISO, this.form().endDate),
    });
  }

  setEndDate(value: Date | string | null): void {
    this.patch({ endDate: value });
  }

  setEndDateSign(value: string): void {
    this.patch({
      endDate: assignmentDateFromOffset(this.form().endDate, { sign: value === '+' ? '+' : '-' }),
    });
  }

  setEndDateAmount(value: unknown): void {
    this.patch({
      endDate: assignmentDateFromOffset(this.form().endDate, { amount: value as number }),
    });
  }

  setEndDateUnit(value: string): void {
    this.patch({
      endDate: assignmentDateFromOffset(this.form().endDate, {
        unit: value === 'h' || value === 'm' ? value : 'd',
      }),
    });
  }

  setSentinel(value: boolean): void {
    this.patch({ sentinel: value });
  }

  setLandsat(value: boolean): void {
    this.patch({ landsat: value });
  }

  setPriority(value: string): void {
    this.patch({ priority: value ?? 'Normal' });
  }

  setCloudCover(value: unknown): void {
    if (value === '' || value === null || value === undefined) {
      this.patch({ maximumCloudCover: null });
      return;
    }
    const parsed = Number(value);
    this.patch({ maximumCloudCover: Number.isFinite(parsed) ? parsed : null });
  }

  setSatelliteProductId(value: string): void {
    this.patch({ satelliteProductId: value });
  }

  setMaxReportsPerRun(value: unknown): void {
    const parsed = Number(value);
    this.patch({ maxReportsPerRun: Number.isFinite(parsed) ? parsed : 0 });
  }

  setAlsoPdf(value: boolean): void {
    this.patch({ alsoPdf: !!value });
  }

  setLocale(value: string): void {
    this.patch({ locale: value });
  }

  setBucket(value: string): void {
    this.patch({ bucket: value });
  }

  setCatchUpTake(value: unknown): void {
    const parsed = Number(value);
    this.patch({ catchUpTake: Number.isFinite(parsed) ? parsed : 0 });
  }

  setFailEvery(value: unknown): void {
    const parsed = Number(value);
    this.patch({ failEvery: Number.isFinite(parsed) ? parsed : 0 });
  }

  setFieldId(value: string | null | undefined): void {
    this.patch({ fieldId: value ?? '' });
  }

  setForceSeasonIndices(value: boolean): void {
    this.patch({ forceSeasonIndices: !!value });
  }

  setPolygonDetectionJobId(value: string): void {
    this.patch({ polygonDetectionJobId: value ?? '' });
  }

  setParametersJson(value: string): void {
    this.patch({ parametersJson: value });
  }

  private resetFor(processKey: string, seedJson?: string | null): void {
    const value = seedJson?.trim()
      ? assignmentStartFormFromJson(processKey, seedJson)
      : defaultAssignmentStartForm(processKey);
    this.form.set(value);
    this.ready.set(canStartAssignment(processKey, value));
  }

  private patch(partial: Partial<AssignmentStartFormValue>): void {
    const value = { ...this.form(), ...partial };
    this.form.set(value);
    this.ready.set(canStartAssignment(this.processKey(), value));
  }
}
